"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { Box } from "@mui/material";
import { logoColors } from "@/theme/tokens";

/**
 * The thin gold-to-blue bar across the top while the next page loads (approved loading design). It starts the moment
 * a link inside Davnai is clicked and finishes when the new page is in place, so a click always answers at once.
 */
export default function NavProgress() {
  const pathname = usePathname();
  const search = useSearchParams();
  const [state, setState] = useState<"idle" | "going" | "done">("idle");
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = (e.target as HTMLElement | null)?.closest?.("a");
      if (!a || a.target === "_blank" || a.hasAttribute("download")) return;
      const href = a.getAttribute("href");
      if (!href || href.startsWith("#") || href.startsWith("mailto:") || href.startsWith("tel:")) return;
      let url: URL;
      try {
        url = new URL(href, window.location.href);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin || url.pathname.startsWith("/api/")) return;
      if (url.pathname === window.location.pathname && url.search === window.location.search) return;
      setState("going");
      if (timer.current) clearTimeout(timer.current);
      // Never leave the bar stuck if a page takes unusually long.
      timer.current = setTimeout(() => setState("idle"), 15000);
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  // A new page is in place: finish the bar, then hide it.
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    const finish = setTimeout(() => setState((s) => (s === "going" ? "done" : s)), 0);
    const hide = setTimeout(() => setState((s) => (s === "done" ? "idle" : s)), 350);
    return () => {
      clearTimeout(finish);
      clearTimeout(hide);
    };
  }, [pathname, search]);

  return (
    <Box
      aria-hidden
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        zIndex: 2000,
        height: 3,
        pointerEvents: "none",
        background: `linear-gradient(90deg, ${logoColors.gold}, ${logoColors.blue})`,
        boxShadow: `0 0 8px ${logoColors.blue}`,
        width: state === "idle" ? 0 : state === "going" ? "82%" : "100%",
        opacity: state === "idle" ? 0 : 1,
        transition:
          state === "going" ? "width 6s cubic-bezier(0.1, 0.7, 0.2, 1), opacity 0.1s" : state === "done" ? "width 0.2s ease-out, opacity 0.3s ease 0.15s" : "opacity 0.2s",
      }}
    />
  );
}
